import { useState, useEffect, useMemo } from 'react';
import type { ResourceBudget } from '@/types/budget.types';
import { BudgetCalculationService } from '@/services/budgetCalculation.service';
import type { BudgetCalculationResult } from '@/services/budgetCalculation.service';

export const useBudgetCalculations = (projectId: string, resourceBudget: ResourceBudget | null) => {
  const [calculation, setCalculation] = useState<BudgetCalculationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateVersion, setRateVersion] = useState(0);

  // Listen for rate changes in localStorage
  useEffect(() => {
    console.log('[useBudgetCalculations] Setting up event listeners for projectId:', projectId);
    
    const handleStorageChange = (e: StorageEvent) => {
      // Check if rate-related keys changed
      console.log('[useBudgetCalculations] Storage event received:', e.key);
      if (e.key && (e.key.includes('projectRates') || e.key.includes('personRates'))) {
        console.log('[useBudgetCalculations] Rate change detected in localStorage, triggering recalculation');
        setRateVersion(prev => {
          console.log('[useBudgetCalculations] Incrementing rateVersion from', prev, 'to', prev + 1);
          return prev + 1;
        });
      }
    };

    // Also listen for custom events (for same-tab changes)
    const handleRateChange = (e: Event) => {
      console.log('[useBudgetCalculations] rateCardUpdated event received:', e);
      const customEvent = e as CustomEvent;
      console.log('[useBudgetCalculations] Event detail:', customEvent.detail);
      setRateVersion(prev => {
        console.log('[useBudgetCalculations] Incrementing rateVersion from', prev, 'to', prev + 1);
        return prev + 1;
      });
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('rateCardUpdated', handleRateChange);
    console.log('[useBudgetCalculations] Event listeners attached');

    return () => {
      console.log('[useBudgetCalculations] Removing event listeners');
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('rateCardUpdated', handleRateChange);
    };
  }, [projectId]);

  // Memoize the calculation to avoid unnecessary recalculations
  const calculatedBudget = useMemo(() => {
    if (!projectId || !resourceBudget) {
      console.log('[useBudgetCalculations] Missing projectId or resourceBudget');
      return null;
    }

    try {
      console.log('[useBudgetCalculations] Calculating budget for project:', projectId, 'rateVersion:', rateVersion);
      const result = BudgetCalculationService.calculateBudget(projectId, resourceBudget);
      console.log('[useBudgetCalculations] Calculation complete:', result);
      return result;
    } catch (err) {
      console.error('[useBudgetCalculations] Calculation error:', err);
      setError(err instanceof Error ? err.message : 'Calculation failed');
      return null;
    }
  }, [projectId, resourceBudget, rateVersion]);

  useEffect(() => {
    setCalculation(calculatedBudget);
  }, [calculatedBudget]);

  /**
   * Calculate the impact of a rate change
   */
  const calculateRateChangeImpact = (
    roleId: string,
    newCostRate: number,
    newBillRate: number,
    effectiveFrom: string
  ) => {
    if (!resourceBudget) {
      return {
        revenueImpact: 0,
        costImpact: 0,
        marginImpact: 0,
        affectedMonths: [],
        remainingHours: 0,
      };
    }

    return BudgetCalculationService.calculateRateChangeImpact(
      projectId,
      resourceBudget,
      roleId,
      newCostRate,
      newBillRate,
      effectiveFrom
    );
  };

  /**
   * Recalculate budget (useful after rate changes)
   */
  const recalculate = () => {
    if (!projectId || !resourceBudget) return;

    setLoading(true);
    try {
      const result = BudgetCalculationService.calculateBudget(projectId, resourceBudget);
      setCalculation(result);
      setError(null);
    } catch (err) {
      console.error('[useBudgetCalculations] Recalculation error:', err);
      setError(err instanceof Error ? err.message : 'Recalculation failed');
    } finally {
      setLoading(false);
    }
  };

  return {
    calculation,
    loading,
    error,
    calculateRateChangeImpact,
    recalculate,
    
    // Convenience accessors
    totalLabourCost: calculation?.totalLabourCost || 0,
    totalRevenue: calculation?.totalRevenue || 0,
    totalMargin: calculation?.totalMargin || 0,
    marginPercentage: calculation?.marginPercentage || 0,
    roleBreakdown: calculation?.roleBreakdown || [],
  };
};
